import { ProjectCard, ProjectCardData } from '../../landing/components/ProjectCard';
import { useTranslation } from '../../../core/hooks/useTranslation';

interface SearchResultsProps {
    results: any[];
    isLoading: boolean;
}

/**
 * Maps raw search unit data to the standardized ProjectCardData structure.
 */
function mapUnitToCardData(unit: any, t: any): ProjectCardData {
    return {
        id: Number(unit.id),
        title: unit.title || '',
        location: unit.project?.title || unit.projectTitle || unit.location || '',
        specs: {
            beds: unit.specs?.beds ?? unit.beds ?? 0,
            baths: unit.specs?.baths ?? unit.baths ?? 0,
            area: `${unit.specs?.area ?? unit.area ?? 0} ${t('common.m2', 'm²')}`
        },
        price: unit.price,
        payment: unit.payment
            ? `${unit.payment} ${t('common.currency', 'EGP')} / ${t('recommended.monthly', 'Month')}`
            : t('common.installments', 'Installments'),
        status: unit.status || '',
        image: unit.image || '',
        constructionStatus: unit.constructionStatus || undefined,
        finishType: unit.finishType || undefined,
        deliveryDate: unit.deliveryDate || undefined,
    };
}

export const SearchResults = ({ results, isLoading }: SearchResultsProps) => {
    const { t } = useTranslation();

    if (isLoading) {
        return (
            <div className="grid grid-cols-1 gap-6 animate-pulse">
                {[1, 2, 3, 4].map((n) => (
                    <div key={n} className="bg-gray-100 rounded-2xl h-[300px]" />
                ))}
            </div>
        );
    }

    if (results.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 px-6 text-center">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6">
                    <span className="text-3xl">🔍</span>
                </div>
                <h3 className="text-xl font-bold text-secondary mb-2">{t('search.results.notFound', 'No Properties Found')}</h3>
                <p className="text-gray-500 max-w-md">
                    {t('search.results.tryAdjusting', "We couldn't find any properties matching your criteria. Try adjusting your filters or search for something else.")}
                </p>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-4">
            {results.map((unit) => {
                const projectData = mapUnitToCardData(unit, t);

                return (
                    // Responsive Layout: Standard (Vertical) on Mobile, Horizontal on Desktop
                    <div key={unit.id}>
                        {/* Mobile: Standard Layout */}
                        <div className="block md:hidden">
                            <ProjectCard
                                project={projectData}
                                linkTo={`/units/${unit.slug || unit.id}`}
                                layout="standard"
                                type="Unit"
                            />
                        </div>
                        {/* Desktop: Horizontal Layout */}
                        <div className="hidden md:block">
                            <ProjectCard
                                project={projectData}
                                linkTo={`/units/${unit.slug || unit.id}`}
                                layout="horizontal-mobile"
                                type="Unit"
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};
